import { useState, useCallback } from 'react'

const isBrowser = typeof window !== 'undefined'

const getFromStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = isBrowser && window.localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch {
    return defaultValue
  }
}

interface UseStoredStateOptions<T> {
  key: string
  defaultValue: T
}

type SetValue<T> = T | ((prevValue: T) => T)

export const useStoredState = <T>({
  key,
  defaultValue,
}: UseStoredStateOptions<T>): [T, (value: SetValue<T>) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => getFromStorage(key, defaultValue))

  const setValue = useCallback(
    (value: SetValue<T>) => {
      try {
        const currentValue = getFromStorage(key, defaultValue)
        const valueToStore = value instanceof Function ? value(currentValue) : value
        if (isBrowser) window.localStorage.setItem(key, JSON.stringify(valueToStore))
        setStoredValue(valueToStore)
      } catch {
        // do nothing
      }
    },
    [key, defaultValue]
  )

  return [storedValue, setValue]
}
